import {useEffect, useState} from 'react';
import {Form, Select} from 'antd';
import {useLazyQuery, useQuery} from '@apollo/client';
import _ from 'lodash';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {UserSelectField} from 'components/userSelectField/UserSelectField';
import {brokerOptionLabel} from '../brokerVenues';
import {GET_ALL_BROKERS} from 'graphql/queries/stocks';
import {GET_ALL_COLLECTIONS} from 'graphql/queries/collections';
import {GET_ASSET_CLASS_INSTRUMENTS} from 'graphql/queries/assetManagement';
import {assetClassKeyTypes} from 'pages/AssetManagement/modules/constants';
import {INSTRUMENT_KEY_LABELS} from 'pages/AssetManagement/modules/instrumentKeys';
import styles from './ordersMetrics.module.scss';

/**
 * The filter panel for the Metrics tab.
 *
 * This used to be the order queues' `OrderFilter`, which collects around
 * thirty fields — but `OrdersMetricsInput` accepts sixteen, and most of the
 * difference is not a naming mismatch, it is a dimension the aggregation does
 * not have. Status is the clearest case: metrics reports every lifecycle stage
 * at once, so "status: PENDING" cannot narrow anything.
 *
 * Reusing that panel meant offering controls that did nothing and then
 * printing a notice apologising for them. This offers only what the query
 * accepts, so anything set here is honoured.
 *
 * Not here on purpose, because the tab already carries them:
 *  - `stockExchange` — the exchange lens above the results
 *  - `startDate` / `endDate` / `dateField` — the range and "Applies to"
 *  - `assetCurrency`, `includeDerivatives`, `brokerMatch` — the toolbar
 */

/** The instrument types orders are actually placed against. */
const INSTRUMENT_KEYS = ['PUBLIC_EQUITY', 'BOND', 'MUTUAL_FUND', 'IPO'];

export const MetricsFilter = ({
  open,
  values,
  user,
  setUser,
  onApply,
  onReset,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [instruments, setInstruments] = useState([]);

  const instrumentKey = Form.useWatch('instrumentKey', form);
  const assetClass = Form.useWatch('assetClass', form);

  const {data: brokers} = useQuery(GET_ALL_BROKERS, {
    skip: !open,
    errorPolicy: 'all',
  });
  const {data: collections, loading: loadingCollections} = useQuery(
    GET_ALL_COLLECTIONS,
    {skip: !open, variables: {filters: {all: true}}, errorPolicy: 'all'},
  );

  const [fetchInstruments, {refetch: refetchInstruments}] = useLazyQuery(
    GET_ASSET_CLASS_INSTRUMENTS,
  );

  // The instrument list is only meaningful once a type and class narrow it —
  // the same pairing the order filter requires.
  useEffect(() => {
    if (!instrumentKey || !assetClass) {
      setInstruments([]);
      return;
    }
    fetchInstruments({
      variables: {
        input: {explore: false, instrumentKey, assetClassKey: assetClass},
      },
      fetchPolicy: 'no-cache',
      onCompleted: data =>
        setInstruments(data?.getFinancialInstruments?.data || []),
    });
  }, [instrumentKey, assetClass]);

  // Re-seed from the applied values every time the panel opens, so it always
  // reflects what is actually in effect.
  useEffect(() => {
    if (open) form.setFieldsValue(values || {});
  }, [open]);

  const searchInstruments = _.debounce(async search => {
    if (!instrumentKey || !assetClass) return;
    try {
      const {data} = await refetchInstruments({
        input: {
          explore: false,
          instrumentKey,
          assetClassKey: assetClass,
          search,
          page: 1,
          limit: 100,
        },
      });
      setInstruments(data?.getFinancialInstruments?.data || []);
    } catch (error) {
      // A failed search just leaves the previous list in place.
    }
  }, 800);

  return (
    <div className={styles.panel}>
      <div className={styles.panelHead}>
        <div>
          <h3>Filter metrics</h3>
          <p>Every field here narrows the figures.</p>
        </div>
        <button
          type="button"
          className={styles.panelClose}
          aria-label="Close filters"
          onClick={onClose}>
          ×
        </button>
      </div>

      <Form
        form={form}
        layout="vertical"
        className={styles.panelForm}
        onFinish={onApply}>
        <div className={styles.panelBody}>
          <div className={styles.panelSection}>
            <h4>What was traded</h4>
            <div className={styles.panelGrid}>
              <Form.Item name="instrumentKey" label="Instrument type">
                <Select allowClear placeholder="Any type">
                  {INSTRUMENT_KEYS.map(key => (
                    <Select.Option key={key} value={key}>
                      {INSTRUMENT_KEY_LABELS[key] ?? key}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="assetClass" label="Asset class">
                <Select
                  allowClear
                  placeholder="Any class"
                  optionFilterProp="label">
                  {assetClassKeyTypes.map(item => (
                    <Select.Option
                      key={item.value}
                      value={item.value}
                      label={item.label}>
                      {item.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="instrumentId"
                label="Instrument"
                className={styles.panelFull}
                extra={
                  instrumentKey && assetClass
                    ? undefined
                    : 'Pick an instrument type and asset class first.'
                }>
                <Select
                  allowClear
                  showSearch
                  filterOption={false}
                  disabled={!instrumentKey || !assetClass}
                  placeholder="Any instrument"
                  onSearch={searchInstruments}>
                  {instruments.map(item => (
                    <Select.Option key={item?.id} value={item?.id}>
                      {item?.name ?? item?.title}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>

          <div className={styles.panelSection}>
            <h4>Trade shape</h4>
            <div className={styles.panelGrid}>
              <Form.Item name="direction" label="Direction">
                <Select allowClear placeholder="Buy and sell">
                  <Select.Option value="BUY">Buy</Select.Option>
                  <Select.Option value="SELL">Sell</Select.Option>
                </Select>
              </Form.Item>
              <Form.Item name="type" label="Order type">
                <Select allowClear placeholder="Any type">
                  <Select.Option value="MARKET">Market</Select.Option>
                  <Select.Option value="LIMIT">Limit</Select.Option>
                </Select>
              </Form.Item>
            </div>
          </div>

          <div className={styles.panelSection}>
            <h4>Who</h4>
            <div className={styles.panelGrid}>
              <Form.Item
                label="Broker"
                name="broker"
                extra="Whose desk the orders are on — not who filled them.">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder="Any broker">
                  {(brokers?.getBrokers?.data ?? []).map(broker => (
                    <Select.Option key={broker?.id} value={broker?.id}>
                      {brokerOptionLabel(broker)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item label="User" className={styles.panelUser}>
                <UserSelectField user={user} setUser={setUser} />
              </Form.Item>
              <Form.Item
                name="collectionId"
                label="Collection"
                className={styles.panelFull}>
                <Select
                  allowClear
                  showSearch
                  loading={loadingCollections}
                  optionFilterProp="label"
                  placeholder="Any collection">
                  {(
                    collections?.adminGetAllCollections?.data?.collections ?? []
                  ).map(collection => (
                    <Select.Option
                      key={collection?.id}
                      value={collection?.id}
                      label={collection?.name}>
                      {collection?.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
        </div>

        <div className={styles.panelFoot}>
          <button
            type="button"
            className={styles.panelReset}
            onClick={() => {
              form.resetFields();
              setUser?.(null);
              onReset();
            }}>
            Reset all
          </button>
          <SubmitButton label="Apply filters" />
        </div>
      </Form>
    </div>
  );
};

export default MetricsFilter;
